import { Avatar, Grid, Typography, Link, Button, IconButton, Tooltip, Box } from '@mui/material';
import { IComponentCss, MdView } from '@tap/mui-ui';
import { useFetchData } from '@tap/mui-ui';
import { commentService, userService, IApiComment, IApiUserSession, accountService } from '@tap/data-providers';
import { AddCommentForm } from './AddCommentForm';
import { EditCommentDialog } from './EditCommentDialog';
import { DeleteCommentDialog } from './DeleteCommentDialog';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { ICancelDialogResult, useDialog } from '@tap/tap-ui';
import React from 'react';
import { UseCommentState } from './useComment';
import { dateTimeHelper } from '@tap/utils';

/*
*   Комментарии
*/

type CommentListProps = {
    currentUser: IApiUserSession | undefined,
    commentState: UseCommentState,
    documentKey: string,
    reloadIndex?: number,
}

type ICommentProps = {
    c: IApiComment,
    currentUserId: number | undefined,
    handleOpenDialog: (dialogId: DialogIds, comment: IApiComment) => void,
}

enum DialogIds {
    Edit = 'edit',
    Delete = 'delete',
}

const css: IComponentCss = {
    commentList: {
        mt: -2,
    },
    noComments: {
        mt: 2,
    },
    comment: {
        position: 'relative',
        padding: '8px 0 0 30px',
        borderBottom: ' 1px solid #e0e0e0',
        ':hover': {
            '[class*=isShowCommentButtons]': {
                display: 'flex',
            },
        },
    },
    commentHeader: {
        position: 'relative',
        padding: '0px 60px 0px 0px',
    },
    avatar: {
        position: 'absolute',
        width: 18,
        height: 18,
        fontSize: '11px',
        left: 0,
        top: '9px',
    },
    author: {
        fontWeight: 700,
        fontSize: '14px',
        marginRight: '15px',
    },
    date: {
        fontSize: '14px',
        color: '#999999',
    },
    dateChangedText: {
        fontSize: '11px',
        color: '#de350b',
        textTransform: 'uppercase',
        marginTop: '3px',
        marginLeft: '15px',
    },
    commentButtonsWrap: {
        position: 'absolute',
        right: 0,
        top: '-3px',
        display: 'none',
    },
    commentButton: {
        color: '#999999',
        marginLeft: '2px',
        '> svg': {
            fontSize: '18px',
        },
    },
    newCommentBlock: {
        marginTop: '16px',
    },
    actionButton: {
        backgroundColor: '#F5F5F5',
        color: '#212121',
        marginRight: '10px',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#EBEBEB !important',
        },
    },
}


{/* Шаблон комментария */ }
function Comment({
    c,
    currentUserId,
    handleOpenDialog,
}: ICommentProps) {
    return (
        <Grid container sx={css.comment}>
            <Avatar sx={css.avatar}>{c.author ? userService.getUserFIO(c.author)[0] : '?'}</Avatar>
            <Grid container sx={css.commentHeader}>
                <Grid container>
                    <Typography
                        component={Link}
                        sx={css.author}
                    >
                        {c.author ? userService.getUserFIO(c.author) : c.authorId}
                    </Typography>

                    <Typography sx={css.date}>
                        {dateTimeHelper.format(c.createdAtUtc, 'dd MMM yyyy в HH:mm', true)}
                    </Typography>

                    {(c?.changedAtUtc && c.changedAtUtc !== c.createdAtUtc) &&
                        <>
                            <Tooltip title={dateTimeHelper.format(c.changedAtUtc, 'dd MMM yyyy в HH:mm', true)}>
                                <Typography sx={css.dateChangedText}>
                                    Изменён
                                </Typography>
                            </Tooltip>

                        </>
                    }
                </Grid>

                {currentUserId === c.authorId &&
                    <Grid sx={css.commentButtonsWrap} className='isShowCommentButtons'>
                        <IconButton
                            size='small'
                            sx={css.commentButton}
                            onClick={() => handleOpenDialog(DialogIds.Edit, c)}
                        >
                            <EditOutlinedIcon />
                        </IconButton>
                        <IconButton
                            size='small'
                            sx={css.commentButton}
                            onClick={() => handleOpenDialog(DialogIds.Delete, c)}
                        >
                            <ClearOutlinedIcon />
                        </IconButton>
                    </Grid>
                }
            </Grid>
            <MdView mdContent={c.message || ''} />
        </Grid >
    );
}


{/* Список комментариев */ }
export const CommentList = ({
    commentState,
    currentUser,
    documentKey,
}: CommentListProps) => {
    const currentUserId = currentUser?.userInfo?.id;

    const [commentListState] = useFetchData(async () => {
        const commentListResponse = await commentService.getCommentList(documentKey);
        const autorIds = commentListResponse.map(c => c.authorId).filter(id => id > 0);
        if (autorIds.length === 0)
            return commentListResponse;

        const users = await accountService.getUserAccountById([...new Set(autorIds)]);
        commentListResponse.forEach(c_1 => {
            c_1.author = users.find(u => u.id === c_1.authorId);
        });
        return commentListResponse;
    }, [documentKey]);

    const handleCloseDialog = ({ isCancel }: ICancelDialogResult) => {
        if (!isCancel) {
            commentListState.fetchData()
        }
    };

    const handleOpenDialog = (dialogId: DialogIds, comment: IApiComment) => {
        switch (dialogId) {
            case DialogIds.Edit:
                EditCommentReadyDialog.open({
                    comment,
                    documentKey,
                    editCommentCallback: () => commentListState.fetchData(),
                })
                break;
            case DialogIds.Delete:
                DeleteCommentReadyDialog.open({
                    comment,
                    deleteCommentCallback: () => commentListState.fetchData(),
                })
                break;
        }
    };

    const EditCommentReadyDialog = useDialog(EditCommentDialog, handleCloseDialog);
    const DeleteCommentReadyDialog = useDialog(DeleteCommentDialog, handleCloseDialog);


    if (!commentListState.data)
        return null;

    return (
        <>
            <Grid sx={css.commentList}>
                {commentListState.data.length > 0
                    ? commentListState.data.map(c =>
                        <Comment
                            key={c.id}
                            c={c}
                            currentUserId={currentUserId}
                            handleOpenDialog={handleOpenDialog}
                        />,
                    )
                    : <Typography sx={css.noComments}>Нет комментариев</Typography>
                }
            </Grid>
            <Grid sx={css.newCommentBlock}>
                {!commentState.addCommentShow && (
                    <Button
                        color='primary'
                        variant='contained'
                        disableElevation
                        sx={css.actionButton}
                        startIcon={<CommentOutlinedIcon />}
                        onClick={commentState.showAddComment}
                    >
                        Добавить комментарий
                    </Button>
                )}

                <Box sx={commentState.addCommentShow ? {} : { overflow: 'hidden', height: 0 }}>
                    <AddCommentForm
                        commentState={commentState}
                        addNewCommentCallback={() => commentListState.fetchData()}
                        handleHideNewCommentForm={() => commentState.hideAddComment()}
                    />
                </Box>

                <EditCommentReadyDialog.Component />
                <DeleteCommentReadyDialog.Component />
            </Grid>
        </>
    );
}
